import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { Subject, takeUntil, filter } from 'rxjs';
import { CompanyService, Company } from '../../../services/company.service';
import { LoggerService } from '../../../services/logger.service';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { formatCurrencyWithSymbol } from '../../../utils/currency.utils';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';

/**
 * Who pays the service fee. Shares a page with the PIN settings but keeps its
 * own form, so both cards can be edited and stored independently.
 */
@Component({
  selector: 'app-fees-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SaveButtonComponent,
    HlmCardImports,
    HlmItemImports,
    HlmTypographyImports,
    HlmSwitchImports,
  ],
  // Inline hosts ignore the vertical margin of the page's `space-y`.
  host: { class: 'block' },
  templateUrl: './fees-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FeesSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private initialValue = false;

  loading = false;
  formChanged = false;
  company: Company | null = null;

  /** Who carries which fee in a network, for this network's fees (backend: member_payout_cents). */
  fees: { name: string; detail: string }[] = [];

  @ViewChild('saveButton') saveButton!: SaveButtonComponent;

  feesForm: FormGroup;

  constructor(
    private companyService: CompanyService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.feesForm = this.fb.group({
      company_covers_customer_fee: [false],
    });

    this.feesForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formChanged = this.feesForm.value.company_covers_customer_fee !== this.initialValue;
    });
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((c) => c !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.populateForm(company);
        this.describeNetworkFees(company);
      });
  }

  private populateForm(company: Company) {
    this.initialValue = company.company_covers_customer_fee || false;
    this.feesForm.patchValue(
      { company_covers_customer_fee: this.initialValue },
      { emitEvent: false },
    );
    this.formChanged = false;
  }

  private describeNetworkFees(company: Company) {
    const rate = company.fee_rate ?? 0.05;
    const serviceFee = company.customer_fee_cents ?? 99;
    const percent = `${(rate * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;

    this.fees = [
      {
        name: 'Schenkwerk-Gebühr',
        detail:
          `${percent} vom Gutscheinwert. Wird beim Verkauf einbehalten und von dir als ` +
          'Zentrale getragen. Mitglieder erhalten den vollen Einlösewert.',
      },
      {
        name: 'Servicegebühr',
        detail:
          serviceFee > 0
            ? `${formatCurrencyWithSymbol(serviceFee)} je Bestellung, zahlt immer die Kundschaft ` +
              'zusätzlich. Zentrale und Mitglieder tragen davon nichts.'
            : 'Für dein Netzwerk fällt keine Servicegebühr an.',
      },
      {
        name: 'Interne Gutscheine',
        detail: 'Keine Gebühren. Du zahlst dem Mitglied den vollen Einlösewert.',
      },
    ];
  }

  onSubmit() {
    this.loading = true;
    this.saveButton.hideMessages();

    // Only this card's field is sent, so saving here cannot overwrite the PIN.
    this.companyService.updateCompanyDetails(this.feesForm.value).subscribe({
      next: () => {
        this.loading = false;
        this.initialValue = this.feesForm.value.company_covers_customer_fee;
        this.formChanged = false;
        this.saveButton.showSuccessMessage();
      },
      error: (error) => {
        const message = error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving fee settings:', error);
        this.loading = false;
        this.saveButton.showErrorMessage(message);
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
